import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { TekPointFeatureFactory } from './TekPointFeatureFactory';
import { makeObservable, observable } from 'mobx';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorLayer from 'ol/layer/Vector';
import * as olStyle from 'ol/style';

import trpilySvg from '@assets/tek_icons/trpily.svg';
import paSvg from '@assets/tek_icons/pa.svg';
import siSvg from '@assets/tek_icons/si.svg';
import noSvg from '@assets/tek_icons/no.svg';
import neSvg from '@assets/tek_icons/ne.svg';
import poSvg from '@assets/tek_icons/po.svg';
import keSvg from '@assets/tek_icons/ke.svg';
import cucpSvg from '@assets/tek_icons/cucp.svg';
import hpSvg from '@assets/tek_icons/hp.svg';
import koSvg from '@assets/tek_icons/ko.svg';
import vpSvg from '@assets/tek_icons/vp.svg';
import heSvg from '@assets/tek_icons/he.svg';
import pmSvg from '@assets/tek_icons/pm.svg';

const blackCircleSvg =
    'data:image/svg+xml;base64,' +
    btoa(`
<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
 <circle cx="50" cy="50" r="40" fill="black" stroke="black" stroke-width="2"/>
</svg>
`);

export class TekPointsLayer extends LayerWrapper<IOutlinePoint> {
    featureType: FeatureType = 'tek_point';
    @observable public labelProps: string[] = [];
    @observable showLinePoints = false;
    labelsLayer: VectorLayer<any> = null;

    constructor(opts: ILayerWrapperOptions<IOutlinePoint>) {
        opts.layerBuilderFn = () =>
            new WebGLPointsLayer({
                style: {
                    filter: ['!=', ['get', 'hidden', 'string'], 'true'],
                    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 17, 0, 17, 7],
                    'circle-stroke-color': 'rgba(0,0,0,0.1)',
                    'circle-radius': ['interpolate', ['linear'], ['zoom'], 17, 1, 17, 5],
                    'circle-fill-color': ['match', ['get', 'hover'], 1, '#000000', '#000000'],
                    'circle-opacity': 1,
                    'circle-scale': ['interpolate', ['exponential', 2], ['zoom'], 5, 0.3, 15, 1],
                },
            });
        super(opts);
        makeObservable(this);
    }

    private getScaleForZoom(zoom: number): number {
        const minZoom = 5;
        const maxZoom = 240;
        const minScale = 0.025;
        const maxScale = 0.2;

        const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));

        const normalizedZoom = (clampedZoom - minZoom) / (maxZoom - minZoom);
        const scale = maxScale - normalizedZoom * (maxScale - minScale);

        return scale;
    }

    private createSvgStyle(svgPath: string): olStyle.Style {
        const zoom = this.map.getView().getZoom();
        const scale = this.getScaleForZoom(zoom);

        return new olStyle.Style({
            image: new olStyle.Icon({
                src: svgPath,
                scale: scale,
                size: [100, 100],
                anchor: [0.5, 0.5],
            }),
        });
    }

    onAddedToMap() {
        if (!this.labelsLayer) {
            this.labelsLayer = new VectorLayer({
                source: this.layerSource,
                updateWhileAnimating: false,
                updateWhileInteracting: false,
                className: 'tek-points-layer',
                zIndex: 100,
            });

            this.labelsLayer.setStyle(feature => {
                const data = feature.get('data');
                if (!data) return null;

                const isLinePoint = data.isLinePoint;
                const currentZoom = this.map.getView().getZoom();

                // Below minZoom (14), show fixed-size black dots
                if (currentZoom < 14) {
                    return new olStyle.Style({
                        image: new olStyle.Circle({
                            radius: 3,
                            fill: new olStyle.Fill({
                                color: 'black',
                            }),
                            stroke: new olStyle.Stroke({
                                color: 'black',
                                width: 1,
                            }),
                        }),
                    });
                }

                // Above minZoom (14), show scaled SVG icons
                if (data.props && data.props.tutkimustapa) {
                    const tt = data.props.tutkimustapa.split(/\s+/)[0];
                    switch (tt) {
                        case 'HE':
                            return this.createSvgStyle(heSvg);
                        case 'KO':
                            return this.createSvgStyle(koSvg);
                        case 'VP':
                            return this.createSvgStyle(vpSvg);
                        case 'HP':
                            return this.createSvgStyle(hpSvg);
                        case 'PO':
                            return this.createSvgStyle(poSvg);
                        case 'TR':
                        case 'PI':
                        case 'LY':
                            return this.createSvgStyle(trpilySvg);
                        case 'SI':
                            return this.createSvgStyle(siSvg);
                        case 'NE':
                            return this.createSvgStyle(neSvg);
                        case 'KE':
                            return this.createSvgStyle(keSvg);
                        case 'NO':
                            return this.createSvgStyle(noSvg);
                        case 'PA':
                            return this.createSvgStyle(paSvg);
                        case 'CU':
                        case 'CP':
                            return this.createSvgStyle(cucpSvg);
                        case 'PM':
                            return this.createSvgStyle(pmSvg);
                        default:
                            return this.createSvgStyle(blackCircleSvg);
                    }
                }

                const showPoints = !isLinePoint || (isLinePoint && this.showLinePoints);
                return TekPointFeatureFactory.styleFunction(feature, showPoints ? this.labelProps : []);
            });

            this.map.getView().on('change:resolution', () => {
                this.labelsLayer.getSource().changed();
            });
        }
        this.map.addLayer(this.labelsLayer);
    }

    onRemovedFromMap() {
        this.map.removeLayer(this.labelsLayer);
    }
}
export const getTekPointSvg = (tt: string): string => {
    switch (tt) {
        case 'HE':
            return heSvg;
        case 'KO':
            return koSvg;
        case 'VP':
            return vpSvg;
        case 'HP':
            return hpSvg;
        case 'PO':
            return poSvg;
        case 'TR':
        case 'PI':
        case 'LY':
            return trpilySvg;
        case 'SI':
            return siSvg;
        case 'NE':
            return neSvg;
        case 'KE':
            return keSvg;
        case 'NO':
            return noSvg;
        case 'PA':
            return paSvg;
        case 'CU':
        case 'CP':
            return cucpSvg;
        default:
            return blackCircleSvg;
    }
};
